import { useState, useEffect } from 'react';
import axios from 'axios';
import { getPricingForClosestStore } from '../utils/pricing-helper';
import {
  STORES_LIMIT,
  SEARCH_DISTANCE,
  STORE_SERVICES_URL,
  PRICING_URL,
  TOOL_PRICE_OPTIONS,
  VEHICLE_PRICE_OPTIONS
} from '../utils/constants';

const useApiData = ({
  refresh,
  experienceStore,
  zipCode,
  rentalCategory,
  rentalSubCategory,
  isAVehicle
}) => {

  const [pricingData, setPricingData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // eslint-disable-next-line max-len
  const storeSearchUrl = `${STORE_SERVICES_URL}/?radius=${SEARCH_DISTANCE}&pagesize=${STORES_LIMIT}&zipcode=${zipCode}`;
  let pricingUrl = '';

  const fetchData = async (mounted) => {
    setIsError(false);
    setIsLoading(true);
    try {
      const { data: storeResponse, status: storeStatus, statusText: storeStatusText } = await axios(storeSearchUrl);

      if (storeStatus !== 200) {
        throw Error(`statusCode: ${storeStatus}\nstatusText: ${storeStatusText}\nURL: ${storeSearchUrl}`);
      }

      const rentalStores = (storeResponse
        && storeResponse?.stores
          .sort((storeA, storeB) => {
            return (
              Number.parseFloat(storeA.distance)
              - Number.parseFloat(storeB.distance)
            );
          })
      );

      const rentalStoreIds = rentalStores.map((store) => store.storeId).join(',');

      pricingUrl = `${PRICING_URL}/${rentalCategory}/${rentalSubCategory}/${rentalStoreIds}`;

      const { data: pricingResponse, status: pricingStatus, statusText: pricingStatusText } = await axios(pricingUrl);

      if (pricingStatus !== 200) {
        throw Error(`statusCode: ${pricingStatus}\nstatusText: ${pricingStatusText}\nURL: ${pricingUrl}`);
      }

      const rentalPricingData = (pricingResponse
        && pricingResponse?.map((store) => {
          const { locationNbr, depositAmt } = store;
          const rentalPricing = [];
          let priceOptions;

          if (isAVehicle) {
            priceOptions = VEHICLE_PRICE_OPTIONS;
          } else {
            priceOptions = TOOL_PRICE_OPTIONS;
          }

          Object.entries(priceOptions).forEach(([optionKey, [duration = null, promotion = null]]) => {
            const priceOption = {};
            priceOption.price = parseFloat(store[optionKey]);
            priceOption.duration = duration;

            if (promotion) {
              priceOption.promotion = promotion;
            }
            rentalPricing.push(priceOption);
          });

          return {
            storeId: locationNbr,
            rentalDeposit: { depositAmount: parseFloat(depositAmt) },
            rentalPricing
          };
        })
      );

      if (mounted && rentalStores && rentalPricingData) {
        setPricingData(getPricingForClosestStore({
          experienceStore,
          rentalStores,
          rentalPricingData,
        }));
      }
    } catch (error) {
      setIsError(true);
      LIFE_CYCLE_EVENT_BUS.trigger('rental-api-sources.error', {
        rentalErrorType: 'RentalPricingComponent',
        rentalMessage: error?.message,
        rentalEndpoint: {
          storeSearchUrl,
          pricingUrl
        }
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    if (rentalCategory && rentalSubCategory && zipCode) {
      fetchData(mounted);
    }
    return () => { mounted = false; };
  }, [rentalCategory, rentalSubCategory, zipCode, isAVehicle, refresh]);

  return { pricingData, isLoading, isError };
};

export { useApiData };
